import * as THREE from "three";
import { canvas2d, rng } from "../textures";

// Surface detail for styles that want it (#75, realistic): normal and
// roughness maps made procedurally, no image files. A surface with a color
// texture gets maps derived from that texture's own canvas (so the concrete's
// pores, cracks and oil stains, the plywood's grain and skid marks and the
// paint's chips all catch the light where they're drawn); bare metal gets
// brushed streaks, anything else a fine grain. Null outside a real browser.

export interface DetailMaps {
  normal: THREE.Texture;
  roughness: THREE.Texture;
}

/** Maps derived per source canvas (a map build makes new canvases, so this empties itself). */
const derived = new WeakMap<object, DetailMaps>();

function dataTexture(ctx: CanvasRenderingContext2D): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * Normal and roughness maps from a height picture: `height(x, y)` in 0..1 over
 * a `w` x `h` tile that wraps. `strength` is how steep the bumps read;
 * `rough(height)` is the roughness-map value (it multiplies the material's).
 */
function fromHeights(
  w: number,
  h: number,
  height: Float32Array,
  strength: number,
  rough: (v: number) => number,
): DetailMaps | null {
  const nctx = canvas2d(w, h);
  const rctx = canvas2d(w, h);
  if (!nctx || !rctx) return null;
  const nimg = nctx.createImageData(w, h);
  const rimg = rctx.createImageData(w, h);
  const at = (x: number, y: number) => height[((y + h) % h) * w + ((x + w) % w)]!;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Sobel slopes; the canvas's y runs down the texture's v.
      const dx = at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1);
      const dy = at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1);
      let nx = -dx * strength;
      let ny = dy * strength;
      const len = Math.hypot(nx, ny, 1);
      nx /= len;
      ny /= len;
      const nz = 1 / len;
      const i = (y * w + x) * 4;
      nimg.data[i] = (nx * 0.5 + 0.5) * 255;
      nimg.data[i + 1] = (ny * 0.5 + 0.5) * 255;
      nimg.data[i + 2] = (nz * 0.5 + 0.5) * 255;
      nimg.data[i + 3] = 255;
      const g = Math.max(0, Math.min(1, rough(at(x, y)))) * 255;
      rimg.data[i] = rimg.data[i + 1] = rimg.data[i + 2] = g;
      rimg.data[i + 3] = 255;
    }
  }
  nctx.putImageData(nimg, 0, 0);
  rctx.putImageData(rimg, 0, 0);
  return { normal: dataTexture(nctx), roughness: dataTexture(rctx) };
}

/** A copy of `maps` laid out like `like` (repeat, offset), sharing the pixels. */
function laidOut(maps: DetailMaps, like: THREE.Texture): DetailMaps {
  const out = { normal: maps.normal.clone(), roughness: maps.roughness.clone() };
  for (const t of [out.normal, out.roughness]) {
    t.repeat.copy(like.repeat);
    t.offset.copy(like.offset);
    t.rotation = like.rotation;
    t.needsUpdate = true;
  }
  return out;
}

/**
 * Maps from a color texture's own canvas: darker is lower (cracks, grain,
 * pores sink in) and smoother (oil stains and polished skid marks shine).
 */
export function mapsFromTexture(tex: THREE.Texture, strength = 2): DetailMaps | null {
  const img = tex.image as HTMLCanvasElement | undefined;
  if (!img || typeof img.getContext !== "function") return null;
  let maps = derived.get(img);
  if (!maps) {
    const ctx = img.getContext("2d");
    if (!ctx) return null;
    const { width: w, height: h } = img;
    const px = ctx.getImageData(0, 0, w, h).data;
    const heights = new Float32Array(w * h);
    let mean = 0;
    for (let i = 0; i < w * h; i++) {
      heights[i] = (0.3 * px[i * 4]! + 0.59 * px[i * 4 + 1]! + 0.11 * px[i * 4 + 2]!) / 255;
      mean += heights[i]!;
    }
    mean /= w * h;
    const made = fromHeights(w, h, heights, strength, (v) => 0.85 + (v - mean) * 1.2);
    if (!made) return null;
    maps = made;
    derived.set(img, maps);
  }
  return laidOut(maps, tex);
}

let brushed: DetailMaps | null | undefined;
let grain: DetailMaps | null | undefined;

/** Brushed and scuffed steel: fine streaks along u, a few deeper scuffs. */
/** How far the brushed-steel roughness map scales a material's roughness, darkest streak to brightest. */
export const BRUSHED_ROUGHNESS = { min: 0.75, max: 1.1 } as const;

export function brushedMaps(repeat: number): DetailMaps | null {
  if (brushed === undefined) {
    const W = 256;
    const H = 256;
    const r = rng(23);
    const heights = new Float32Array(W * H);
    for (let y = 0; y < H; y++) {
      // Each row is one streak, drifting a little along its length.
      const base = r() * 0.5;
      for (let x = 0; x < W; x++) heights[y * W + x] = base + Math.sin((x / W) * Math.PI * 2 * 3 + y) * 0.03;
    }
    for (let i = 0; i < 25; i++) {
      let x = Math.floor(r() * W);
      let y = Math.floor(r() * H);
      for (let k = 0; k < 30; k++) {
        heights[((y + H) % H) * W + ((x + W) % W)] = 1;
        x += r() < 0.8 ? 1 : 0;
        y += r() < 0.3 ? (r() < 0.5 ? 1 : -1) : 0;
      }
    }
    brushed = fromHeights(W, H, heights, 0.6, (v) => BRUSHED_ROUGHNESS.min + v * (BRUSHED_ROUGHNESS.max - BRUSHED_ROUGHNESS.min));
  }
  if (!brushed) return null;
  const like = new THREE.Texture();
  like.repeat.set(repeat, repeat);
  return laidOut(brushed, like);
}

/** A fine, even grain for surfaces with no drawing of their own (paint, grass, skin). */
export function grainMaps(repeat: number): DetailMaps | null {
  if (grain === undefined) {
    const S = 256;
    const r = rng(31);
    // Value noise: a coarse random grid, smoothed, plus a little per-pixel speckle.
    const G = 32;
    const grid = Array.from({ length: G * G }, () => r());
    const heights = new Float32Array(S * S);
    const cell = S / G;
    const g = (i: number, j: number) => grid[((j + G) % G) * G + ((i + G) % G)]!;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const fx = x / cell;
        const fy = y / cell;
        const i = Math.floor(fx);
        const j = Math.floor(fy);
        const u = fx - i;
        const v = fy - j;
        const top = g(i, j) * (1 - u) + g(i + 1, j) * u;
        const bottom = g(i, j + 1) * (1 - u) + g(i + 1, j + 1) * u;
        heights[y * S + x] = (top * (1 - v) + bottom * v) * 0.7 + r() * 0.3;
      }
    }
    grain = fromHeights(S, S, heights, 0.8, (v) => 0.85 + (v - 0.5) * 0.3);
  }
  if (!grain) return null;
  const like = new THREE.Texture();
  like.repeat.set(repeat, repeat);
  return laidOut(grain, like);
}

let fingerprint: DetailMaps | null | undefined;

/**
 * Clay's fingerprints (#76): a tile of overlapping thumb presses, each a
 * whorl of fine ridges around its own center, the nearest press winning. The
 * distance to a center wraps round the tile, so the tile repeats seamlessly.
 * Heights are 0..1 (ridge tops at 1); pure, so it's tested without a canvas.
 */
export function fingerprintHeights(size: number): Float32Array {
  const r = rng(47);
  const presses = Array.from({ length: 5 }, () => ({ x: r() * size, y: r() * size, turn: r() * Math.PI * 2, ridges: 0.55 + r() * 0.15 }));
  const out = new Float32Array(size * size);
  const wrap = (d: number) => d - size * Math.round(d / size);
  const px = size / 64;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let best = Infinity;
      let v = 0.5;
      for (const p of presses) {
        const dx = wrap(x - p.x);
        const dy = wrap(y - p.y);
        const d = Math.hypot(dx, dy);
        if (d >= best) continue;
        best = d;
        // Loops rather than rings: the ridge phase bends with the angle round the center.
        const a = Math.atan2(dy, dx) + p.turn;
        v = 0.5 + 0.5 * Math.sin((d / px) * p.ridges * 2 + 1.4 * Math.sin(2 * a));
      }
      out[y * size + x] = v;
    }
  }
  return out;
}

/** Clay's fingerprint normals and a slightly smoother shine on the ridge tops. */
export function fingerprintMaps(repeat: number): DetailMaps | null {
  if (fingerprint === undefined) {
    const S = 256;
    fingerprint = fromHeights(S, S, fingerprintHeights(S), 0.35, (v) => 1 - v * 0.12);
  }
  if (!fingerprint) return null;
  const like = new THREE.Texture();
  like.repeat.set(repeat, repeat);
  return laidOut(fingerprint, like);
}
